import { useEffect, useMemo, useState } from 'react';
import { JobDetail } from 'api';
import {
  addReference,
  EMPTY_DECISION,
  FieldCitationOverride,
  FieldDecisionState,
} from '../decisionState';

export type { FieldCitationOverride, FieldDecisionState } from '../decisionState';

/** Per-field decisions for the selected job. Starts empty on every job change:
 * a previous review is never pre-filled, so a re-review can't be a rubber stamp. */
export const useReviewDecisions = (jobDetail: JobDetail | undefined) => {
  const [decisions, setDecisions] = useState<
    Record<string, FieldDecisionState>
  >({});

  useEffect(() => {
    if (!jobDetail) {
      setDecisions({});
      return;
    }
    const fresh: Record<string, FieldDecisionState> = {};
    jobDetail.fields.forEach(f => {
      fresh[f.fieldName] = { ...EMPTY_DECISION };
    });
    setDecisions(fresh);
    // Only on a job change, not when the same job refetches after a submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDetail?.extractionJobKey]);

  const setDecision = (
    fieldName: string,
    patch: Partial<FieldDecisionState>
  ) => {
    setDecisions(prev => ({
      ...prev,
      [fieldName]: { ...(prev[fieldName] ?? EMPTY_DECISION), ...patch },
    }));
  };

  // Against the latest state, so two quick selections can't overwrite each other.
  const addFieldReference = (fieldName: string, reference: FieldCitationOverride) => {
    setDecisions(prev => {
      const current = prev[fieldName] ?? EMPTY_DECISION;
      return {
        ...prev,
        [fieldName]: {
          ...current,
          citationOverrides: addReference(current.citationOverrides, reference),
        },
      };
    });
  };

  const undecidedCount = useMemo(
    () => Object.values(decisions).filter(d => d.isCorrect == null).length,
    [decisions]
  );

  // Marks every undecided field Correct and leaves decided ones alone.
  const approveAll = () => {
    setDecisions(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(fieldName => {
        if (next[fieldName].isCorrect == null) {
          next[fieldName] = { ...EMPTY_DECISION, isCorrect: true };
        }
      });
      return next;
    });
  };

  return {
    decisions,
    setDecision,
    addFieldReference,
    approveAll,
    undecidedCount,
  };
};
